import { useState, useEffect } from 'react';
import {
	KeyboardAvoidingView,
	Platform,
} from 'react-native';
import {
	Icon,
	Row,
	Text,
} from 'native-base';
// import {
// 	useNavigation,
// 	useRoute,
// } from '@react-navigation/native';
import { GiftedChat } from 'react-native-gifted-chat'; // https://www.npmjs.com/package/react-native-gifted-chat
import { useSelector, useDispatch } from 'react-redux';
import {
	selectUser,
	selectChatIsSocketConnected,
	selectChatCurrentConversation,
	selectChatRecipientIsTyping,
	selectChatRecipientOnlineStatuses,
} from '../../models/Slices/AppSlice';
import oneHatData from '@onehat/data';
import ScreenContainer from '@onehat/ui/src/Components/Container/ScreenContainer';
import Circle from '@onehat/ui/src/Components/Icons/Circle.js';
import TypingIndicator from './TypingIndicator.js';
import ConnectionStatus from './ConnectionStatus.js';
import AppGlobals from '../../AppGlobals.js';
import iconPng from '../../img/icon.png';
import _ from 'lodash';

export default function ChatMessagesScreen(props) {
	const
		// navigation = useNavigation(),
		user = useSelector(selectUser),
		chatIsSocketConnected = useSelector(selectChatIsSocketConnected),
		chatCurrentConversation = useSelector(selectChatCurrentConversation),
		chatRecipientIsTyping = useSelector(selectChatRecipientIsTyping),
		chatRecipientOnlineStatuses = useSelector(selectChatRecipientOnlineStatuses), // to force a rerender
		chatManager = AppGlobals.chatManager,
		ChatRecipients = oneHatData.getRepository('ChatRecipients'),
		ChatMessages = oneHatData.getRepository('ChatMessages'),
		currentRecipientId = chatManager.currentConversation && chatManager.currentConversation.recipient_id,
		[giftedMessages, setGiftedMessages] = useState([]),
		formatAsGiftedChat = () => {
			// Convert @onehat/data entities to GiftedChat message format
			const messages = [];
			_.each(ChatMessages.entities, (chatMessage) => {
				messages.push({
					_id: chatMessage.chat_messages__id,
					text: chatMessage.chat_messages__msg,
					createdAt: chatMessage.getProperty('chat_messages__datetime').getRawValue(),
					user: {
						_id: chatMessage.chat_messages__user_id,
						name: chatMessage.users__full_name,
						avatar: iconPng,
					},
					// image?: string
					// video?: string
					// audio?: string
					system: false,
					sent: true,
					received: true,
					pending: false,
					// quickReplies?: QuickReplies
				});
			});
			setGiftedMessages(messages);
		};

	useEffect(() => {
		formatAsGiftedChat();
		ChatMessages.ons(['changeData', 'load'], formatAsGiftedChat);
		return () => {
			ChatMessages.offs(['changeData', 'load'], formatAsGiftedChat);
		}
	}, []);

	useEffect(() => {
		if (chatCurrentConversation) {
			chatManager.clearBadgesForCurrentRecipient();
		}
	}, [chatCurrentConversation]);

	if (!chatIsSocketConnected) {
		return <ConnectionStatus />;
	}

	if (!user) {
		return null;
	}
	
	const chatProps = {
		messages: giftedMessages, // (Array) - Messages to display
		isTyping: chatRecipientIsTyping, // (Bool) - Typing Indicator state; default false. If you userenderFooter it will override 
		// text (String) - Input text; default is undefined, but if specified, it will override GiftedChat's internal state (e.g. for redux; see notes below)
		// placeholder (String) - Placeholder when text is empty; default is 'Type a message...'
		// messageIdGenerator (Function) - Generate an id for new messages. Defaults to UUID v4, generated by uuid
		user: { // User sending the messages
			_id: user.id,
			name: user.full_name,
			// avatar
		},
		onSend: chatManager.sendMessage, // (Function) - Callback when sending a message
		// alwaysShowSend (Bool) - Always show send button in input text composer; default false, show only when text input is not empty
		// locale (String) - Locale to localize the dates. You need first to import the locale you need (ie. require('dayjs/locale/de') or import 'dayjs/locale/fr')
		// timeFormat (String) - Format to use for rendering times; default is 'LT'
		// dateFormat (String) - Format to use for rendering dates; default is 'll'
		loadEarlier: ChatMessages.totalPages > 1, // Enables the "load earlier messages" button
		// isKeyboardInternallyHandled: true, // Determine whether to handle keyboard awareness inside the plugin. If you have your own keyboard handling outside the plugin set this to false; default is true
		onLoadEarlier: () => { // (Function) - Callback when loading earlier messages
			const pageSize = ChatMessages.pageSize;
			ChatMessages.setPageSize(pageSize + 20);
		},
		isLoadingEarlier: ChatMessages.isLoading, // (Bool) - Display an ActivityIndicator when loading earlier messages
		// renderLoading (Function) - Render a loading view when initializing
		// renderLoadEarlier (Function) - Custom "Load earlier messages" button
		renderAvatar: null, // (Function) - Custom message avatar; set to null to not render any avatar for the message
		// showUserAvatar (Bool) - Whether to render an avatar for the current user; default is false, only show avatars for other users
		// showAvatarForEveryMessage (Bool) - When false, avatars will only be displayed when a consecutive message is from the same user on the same day; default is false
		// onPressAvatar (Function(user)) - Callback when a message avatar is tapped
		// onLongPressAvatar (Function(user)) - Callback when a message avatar is long-pressed
		// renderAvatarOnTop (Bool) - Render the message avatar at the top of consecutive messages, rather than the bottom; default is false
		// renderBubble (Function) - Custom message bubble
		renderTicks: () => { return null; }, // (Function(message)) - Custom ticks indicator to display message status
		// renderSystemMessage (Function) - Custom system message
		// onLongPress (Function(context, message)) - Callback when a message bubble is long-pressed; default is to show an ActionSheet with "Copy Text" (see example using showActionSheetWithOptions())
		// inverted: true, // (Bool) - Reverses display order of messages; default is true
		// renderUsernameOnMessage (Bool) - Indicate whether to show the user's username inside the message bubble; default is false
		// renderMessage (Function) - Custom message container
		// renderMessageText (Function) - Custom message text
		// renderMessageImage (Function) - Custom message image
		// renderMessageVideo (Function) - Custom message video
		// imageProps (Object) - Extra props to be passed to the <Image> component created by the default renderMessageImage
		// videoProps (Object) - Extra props to be passed to the video component created by the required renderMessageVideo
		// lightboxProps (Object) - Extra props to be passed to the MessageImage's Lightbox
		// isCustomViewBottom (Bool) - Determine whether renderCustomView is displayed before or after the text, image and video views; default is false
		// renderCustomView (Function) - Custom view inside the bubble
		// renderDay (Function) - Custom day above a message
		// renderTime (Function) - Custom time inside a message
		renderFooter: () => {
			return chatRecipientIsTyping ? <TypingIndicator /> : null;
		},
		// renderFooter (Function) - Custom footer component on the ListView, e.g. 'User is typing...'; see example/App.js for an example. Overrides default typing indicator that triggers when isTyping is true.
		// renderChatEmpty (Function) - Custom component to render in the ListView when messages are empty
		// renderChatFooter (Function) - Custom component to render below the MessageContainer (separate from the ListView)
		// renderInputToolbar (Function) - Custom message composer container
		// renderComposer (Function) - Custom text input message composer
		// renderActions (Function) - Custom action button on the left of the message composer
		// renderSend (Function) - Custom send button; you can pass children to the original Send component quite easily, for example, to use a custom icon (example)
		// renderAccessory (Function) - Custom second line of actions below the message composer
		// onPressActionButton (Function) - Callback when the Action button is pressed (if set, the default actionSheet will not be used)
		// bottomOffset (Integer) - Distance of the chat from the bottom of the screen (e.g. useful if you display a tab bar)
		// minInputToolbarHeight (Integer) - Minimum height of the input toolbar; default is 44
		// listViewProps (Object) - Extra props to be passed to the messages <ListView>; some props can't be overridden, see the code in MessageContainer.render() for details
		listViewProps: {

		},
		// textInputProps (Object) - Extra props to be passed to the <TextInput>
		// textInputStyle: styles.chatText, // (Object) - Custom style to be passed to the <TextInput>
		multiline: false, // (Bool) - Indicates whether to allow the <TextInput> to be multiple lines or not; default true.
		// keyboardShouldPersistTaps (Enum) - Determines whether the keyboard should stay visible after a tap; see <ScrollView> docs
		onInputTextChanged: chatManager.onInputTextChanged, // (Function) - Callback when the input text changes
		// maxInputLength (Integer) - Max message composer TextInput length
		// parsePatterns (Function) - Custom parse patterns for react-native-parsed-text used to linking message content (like URLs and phone numbers), e.g.:
		// extraData (Object) - Extra props for re-rendering FlatList on demand. This will be useful for rendering footer etc.
		// minComposerHeight (Object) - Custom min-height of the composer.
		// maxComposerHeight (Object) - Custom max height of the composer.
		// scrollToBottom (Bool) - Enables the scroll to bottom Component (Default is false)
		// scrollToBottomComponent (Function) - Custom Scroll To Bottom Component container
		// scrollToBottomOffset (Integer) - Custom Height Offset upon which to begin showing Scroll To Bottom Component (Default is 200)
		// scrollToBottomStyle (Object) - Custom style for Bottom Component container
		// alignTop (Boolean) Controls whether or not the message bubbles appear at the top of the chat (Default is false - bubbles align to bottom)
		// onQuickReply (Function) - Callback when sending a quick reply (to backend server)
		// renderQuickReplies (Function) - Custom all quick reply view
		// quickReplyStyle (StyleProp) - Custom quick reply view style
		// renderQuickReplySend (Function) - Custom quick reply send view
		// shouldUpdateMessage (Function) - Lets the message component know when to update outside of normal cases.
		infiniteScroll: true, // (Bool) - infinite scroll up when reach the top of messages container, automatically call onLoadEarlier function if exist (not yet supported for the web)
	};

	let onlineStatus = 'offline',
		onlineColor = '#f00'; // default to offline
	if (chatManager.isCurrentRecipientOnline()) {
		onlineStatus = 'online';
		onlineColor = '#0f0';
	}
	
	return <ScreenContainer screenName="Chat">
				<Row
					h="30px"
					w="100%"
					justifyContent="center"
					alignItems="center"
					borderBottomWidth={1}
					borderBottomColor="#ccc"
				>
					<Icon
						as={Circle}
						color={onlineColor}
						fontSize={12}
						width={20}
						mr={2}
					/>
					<Text color="#666" fontSize={12}>Recipient is {onlineStatus}</Text>
				</Row>
				<Row flex={1} w="100%">
					<GiftedChat {...chatProps} />
				</Row>
				{ Platform.OS === 'android' && <KeyboardAvoidingView behavior="padding" /> }
			</ScreenContainer>;
}
